import styled from 'styled-components';
import { ThumbnailScroller } from 'styles';

// Shared styles for Conversation components (Scene and DS)

export const Base = styled.div`
	// layout
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;

	// style
	background-color: ${({ theme }) => theme.gray[0]};
	border-radius: 8px;
`;

interface JumpButtonProps {
	isMobile?: boolean;
}

export const JumpButton = styled.div<JumpButtonProps>`
	position: absolute;
	top: ${({ isMobile }) => (isMobile ? '-28px' : '-22px')};
	left: 50%;
	transform: translateX(-50%);
	z-index: 2;

	// i want to shift the icon down by 4px while matching height and width
	// so i reduce height by 4px to 22px
	width: ${({ isMobile }) => (isMobile ? '44px' : '26px')};
	height: ${({ isMobile }) => (isMobile ? '40px' : '22px')};
	padding-top: ${({ isMobile }) => (isMobile ? '4px' : '4px')};

	display: flex;
	align-items: center;
	justify-content: center;
	background: ${({ theme }) => theme.gray[0]};
	border: 1px solid ${({ theme }) => theme.gray[400]};
	&:hover {
		background: ${({ theme }) => theme.gray[100]};
	}
	border-radius: 40px;
`;

export const JumpButtonIcon = styled.div`
	// filter out mouse events on the icon, so that all clicks go to the base
	// otherwise Icon will block clicks on Base, and we won't detect when the user clicks the button
	pointer-events: none;
`;

export const InputArea = styled.div`
	position: relative;
`;

export const ScrollingArea = styled.div`
	position: relative;
	overflow-y: auto;
	overflow-x: hidden;
	// make scrollbar a bit wider
	scrollbar-width: thin;
	&::-webkit-scrollbar {
		width: 8px;
	}
	scrollbar-color: ${({ theme }) => theme.gray[400]} ${({ theme }) => theme.gray[100]};
`;

export const CenteredScrollingArea = styled(ScrollingArea)`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 1;
`;

export const Spacer = styled.div`
	flex: 1;
	min-height: 0;
`;

interface MessageProps {
	isMobile?: boolean;
}

export const SystemMessage = styled.div<MessageProps>`
	// layout
	position: relative;
	padding-left: 10px;
	padding-right: 10px;
	padding-top: 8px;
	margin-left: 8px;
	width: calc(100% - 40px);

	// style
	color: ${({ theme }) => theme.gray[1000]};
	font-size: ${({ isMobile }) => (isMobile ? '16px' : '13px')};
	line-height: ${({ isMobile }) => (isMobile ? '24px' : '20px')};
	user-select: text;
	cursor: text;
`;

export const Attachments = styled(ThumbnailScroller)`
	// layout
	gap: 12px;
	padding-top: 1px;
	margin-bottom: 4px;
`;

export const DragOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: ${({ theme }) => theme.blue[300]}AA;
	border: 2px dashed ${({ theme }) => theme.blue[500]};
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
	z-index: 1000;
	transition: opacity 0.2s ease-in-out;
`;

export const DragOverlayContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
`;

export const DragOverlayText = styled.div<MessageProps>`
	color: ${({ theme }) => theme.gray[1000]};
	font-size: ${({ isMobile }) => (isMobile ? '16px' : '14px')};
	font-weight: 500;
`;

export const StatusMessage = styled.div<MessageProps>`
	display: flex;
	align-items: center;
	gap: 12px;
	padding-top: 10px;
	padding-bottom: 12px;
	padding-left: 14px;
	padding-right: 16px;
	margin: 8px 16px;
	margin-bottom: 20px;
	width: calc(100% - 80px);
	background-color: ${({ theme }) => theme.gray[100]};
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 8px;
	font-size: ${({ isMobile }) => (isMobile ? '16px' : '13px')};
	color: ${({ theme }) => theme.gray[800]};
	animation: backgroundPulse 4s ease-in-out infinite;

	@keyframes backgroundPulse {
		0% {
			background-color: ${({ theme }) => theme.gray[100]};
		}
		50% {
			background-color: ${({ theme }) => theme.gray[200]};
		}
		100% {
			background-color: ${({ theme }) => theme.gray[100]};
		}
	}
`;

export const StatusText = styled.div`
	flex: 1;
	line-height: 1.4;
`;

// Wraps ScrollingArea and the restore-spinner overlay so the overlay only covers
// the scrolling area, not the InputArea below it.
export const ScrollingAreaContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	min-height: 0;
`;

export const RestoreOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: ${({ theme }) => theme.gray[0]};
	pointer-events: none;
	z-index: 1;
`;
